import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaRoute, FaPassport, FaHome, FaFlag, FaGraduationCap, FaBriefcase, FaUserTie, FaUsers, FaQuestionCircle } from 'react-icons/fa';
import './Services.css';
import bridgeImage from '../assets/image2/bridge.jpeg';

const PROFILE_KEYS = ['entrepreneurs', 'workers', 'students', 'family', 'other'];
const PROFILE_ICONS = {
  entrepreneurs: FaUserTie,
  workers: FaBriefcase,
  students: FaGraduationCap,
  family: FaUsers,
  other: FaQuestionCircle
};
const PROFILE_REFS = ['entrepreneurs', 'travailleurs', 'etudiants', 'famille', 'autres'];

const Services = () => {
  const { t } = useTranslation();
  const [visibleSections, setVisibleSections] = useState({
    conseil: false,
    visa: false,
    residence: false,
    nationalite: false
  });
  const [visibleProfiles, setVisibleProfiles] = useState({
    etudiants: false,
    travailleurs: false,
    entrepreneurs: false,
    famille: false,
    autres: false
  });

  const conseilRef = useRef(null);
  const visaRef = useRef(null);
  const residenceRef = useRef(null);
  const nationaliteRef = useRef(null);
  const etudiantsRef = useRef(null);
  const travailleursRef = useRef(null);
  const entrepreneursRef = useRef(null);
  const familleRef = useRef(null);
  const autresRef = useRef(null);
  const profileRefs = { etudiants: etudiantsRef, travailleurs: travailleursRef, entrepreneurs: entrepreneursRef, famille: familleRef, autres: autresRef };

  useEffect(() => {
    const observerOptions = { root: null, rootMargin: '0px', threshold: 0.3 };
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          setVisibleSections((prev) => ({ ...prev, [sectionId]: true }));
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    [conseilRef, visaRef, residenceRef, nationaliteRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    const profileObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const profileId = entry.target.getAttribute('data-profile');
            setVisibleProfiles((prev) => ({ ...prev, [profileId]: true }));
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.2 }
    );
    Object.values(profileRefs).forEach((ref) => {
      if (ref.current) profileObserver.observe(ref.current);
    });

    return () => {
      observer.disconnect();
      profileObserver.disconnect();
    };
  }, []);

  const mediationItems = t('servicesPage.mediation.items', { returnObjects: true });

  return (
    <div className="services-page">
      <section className="page-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-3 text-center">
              <img src="/images/logo.jpeg" alt={t('common.altLogo')} className="hero-logo" />
            </div>
            <div className="col-lg-9 text-center">
              <h1 className="page-title">{t('servicesPage.hero.title')}</h1>
              <p className="page-subtitle">{t('servicesPage.hero.subtitle')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="service-detail-section py-5" ref={conseilRef} data-section="conseil">
        <div className="container">
          <div className="service-card-large">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="service-icon-large"><FaSearch /></div>
                <h2 className="service-card-title">{t('servicesPage.mediation.title')}</h2>
                <p className="service-card-subtitle">{t('servicesPage.mediation.subtitle')}</p>
                <ul className="service-list">
                  {mediationItems.map((item, index) => (
                    <li key={index}><FaRoute className="list-icon" /> {item}</li>
                  ))}
                </ul>
                <Link to="/diagnostic" className="btn btn-service">{t('servicesPage.mediation.cta')}</Link>
              </div>
              <div className="col-lg-6">
                <div className={`service-hover-image-container ${visibleSections.conseil ? 'visible' : ''}`}>
                  <img src={bridgeImage} alt="" className="service-hover-image" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {[
        { key: 'visa', ref: visaRef, icon: FaPassport, image: '/images/Screenshot 2026-01-30 094510.png', bg: 'bg-light' },
        { key: 'residence', ref: residenceRef, icon: FaHome, image: '/images/residence.png', bg: '' },
        { key: 'nationality', ref: nationaliteRef, icon: FaFlag, image: '/images/nationalite.png', bg: 'bg-light' }
      ].map(({ key, ref, icon: Icon, image, bg }) => {
        const items = t(`servicesPage.${key}.items`, { returnObjects: true });
        return (
          <section key={key} className={`service-item-section py-5 ${bg}`} ref={ref} data-section={key === 'nationality' ? 'nationalite' : key}>
            <div className="container">
              <div className="service-item-card-with-image">
                <div className="row align-items-center">
                  <div className="col-lg-6">
                    <div className="service-item-icon"><Icon /></div>
                    <h3 className="service-item-title">{t(`servicesPage.${key}.title`)}</h3>
                    <ul className="service-item-list">
                      {items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <div className={`service-hover-image-container ${visibleSections[key === 'nationality' ? 'nationalite' : key] ? 'visible' : ''}`}>
                      <img src={image} alt={t(`servicesPage.${key}.title`)} className="service-hover-image" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="profiles-section py-5">
        <div className="container">
          <h2 className="section-title text-center mb-5">{t('servicesPage.profiles.title')}</h2>
          <div className="row g-4">
            {PROFILE_KEYS.map((profileKey, index) => {
              const Icon = PROFILE_ICONS[profileKey];
              const refKey = PROFILE_REFS[index];
              const items = t(`servicesPage.profiles.${profileKey}.items`, { returnObjects: true });
              return (
                <div key={profileKey} className="col-lg-4 col-md-6">
                  <div
                    ref={profileRefs[refKey]}
                    data-profile={refKey}
                    className={`profile-card ${visibleProfiles[refKey] ? 'profile-visible' : ''}`}
                  >
                    <div className="profile-icon-wrapper">
                      <div className="profile-icon"><Icon /></div>
                    </div>
                    <h4 className="profile-title">{t(`servicesPage.profiles.${profileKey}.title`)}</h4>
                    <ul className="profile-list">
                      {items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
